import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/base/Seo";
import VideoCard from "@/components/feature/VideoCard";
import { videos } from "@/mocks/videos";
import { categories } from "@/mocks/categories";

export default function SearchPage() {
  const [params] = useSearchParams();
  const q = (params.get("q") || "").trim();

  const results = useMemo(() => {
    if (!q) return [];
    const kw = q.toLowerCase();
    return videos.filter((v) => {
      const cat = categories.find((c) => c.id === v.categoryId);
      return (
        v.title.toLowerCase().includes(kw) ||
        v.summary.toLowerCase().includes(kw) ||
        v.tags.some((t) => t.toLowerCase().includes(kw)) ||
        (cat?.name.toLowerCase().includes(kw) ?? false)
      );
    });
  }, [q]);

  return (
    <Layout>
      <Seo
        title={q ? `搜索“${q}”的结果` : "站内搜索"}
        description={`在吃瓜视频网搜索“${q}”相关的明星动态、综艺片段、影视花絮与红人视频。`}
        keywords="吃瓜视频网,搜索,娱乐八卦,圈内速递"
        canonical={`https://soulhandmade.cn/search?q=${encodeURIComponent(q)}`}
      />

      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
        <nav className="flex items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-600">首页</Link>
          <i className="ri-arrow-right-s-line" />
          <span className="text-foreground-700">搜索</span>
        </nav>

        <div className="mt-6">
          <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            {q ? <>搜索 “<span className="text-primary-600">{q}</span>” 的结果</> : "站内搜索"}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            {q ? `共找到 ${results.length} 条相关圈内速递` : "输入关键词，找到你关心的娱乐圈动态。"}
          </p>
        </div>

        {results.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {results.map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        ) : (
          <div className="mt-12 flex flex-col items-center rounded-2xl bg-background-100 px-4 py-16 text-center ring-1 ring-inset ring-background-200">
            <i className="ri-search-line text-5xl text-primary-300" />
            <h2 className="mt-4 font-heading text-xl font-bold text-foreground-900">
              {q ? "没有找到相关速递" : "还没有搜索内容"}
            </h2>
            <p className="mt-2 max-w-md text-sm text-foreground-600">
              {q
                ? "换个关键词试试，或浏览热门栏目寻找你感兴趣的内容。"
                : "在上方搜索框输入明星、综艺、影视等关键词，开始吃瓜。"}
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              {categories.map((c) => (
                <Link
                  key={c.id}
                  to={`/columns/${c.slug}`}
                  className="rounded-full bg-background-50 px-4 py-2 text-sm text-foreground-700 ring-1 ring-inset ring-background-200 transition hover:bg-primary-50 hover:text-primary-700"
                >
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}